//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import * as Progress from '@radix-ui/react-progress';
import React from 'react';
import styled from 'styled-components';

import { DefaultIconButton } from '@components/atoms/button/Button';
import Text, { CaptionErrorText } from '@components/atoms/text/Text';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { Upload } from '@content-drawer/upload/UploadContextProvider';
import { Mixins } from '@theme/mixins';

export interface FileUploadProgressProps {
  onCancel: (upload: Upload) => void;
  upload: Upload;
}

export const FileUploadProgress = ({
  onCancel,
  upload,
}: FileUploadProgressProps) => {
  const progress = Math.min(100, Math.max(0, upload.progress ?? 0)) * 100;

  // We need to show the progress for both items that are uploaded as well as imported from a url(example: Dropbox)
  // Also if the item is uploaded, then we get the file name from the actual File object
  // and for items imported from a URL, we get the file name from the Upload.urlImport object
  const isUrlImport = !!upload.urlImport;
  const fileName = upload.file?.name ?? upload.urlImport?.fileName

  return (
    <Wrapper>
      {(upload.file || isUrlImport) && (
        <>
          <ProgressBar>
            <Text
              ellipsize={1}
              mixin={Mixins.Text.CaptionSecondary}
              title={fileName}
            >
              {fileName}
            </Text>
            {upload.error && (
              <CaptionErrorText>{upload.error}</CaptionErrorText>
            )}
            {!upload.error && (
              <ProgressRoot value={progress}>
                <ProgressIndicator progress={progress} />
              </ProgressRoot>
            )}
          </ProgressBar>
          {!isUrlImport && <DefaultIconButton
            iconName='close'
            onClick={() => onCancel(upload)}
            aria-label={`Cancel ${fileName} upload`}
            trackingId={ContentDrawerTrackingIds.CANCEL_UPLOAD}
          />}
        </>
      )}
    </Wrapper>
  );
};

interface ProgressIndicatorProps {
  progress: number;
}

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[3]};
    width: 100%;
    padding: ${theme.spacings[3]};
  `,
);

const ProgressBar = styled.div`
  width: 100%;
`;

const ProgressRoot = styled(Progress.Root)(
  ({ theme }) => `
    position: relative;
    width: 100%;
    height: ${theme.spacings[2]};
    border: ${theme.borderWidths[1]} solid ${theme.colors.teal1};
    border-radius: ${theme.borderRadii.pill};
    background: ${theme.colors.white};
    overflow: hidden;

    /* Fix overflow clipping in Safari */
    /* https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0 */
    transform: translateZ(0);
  `,
);

const ProgressIndicator = styled(Progress.Indicator)<ProgressIndicatorProps>(
  ({ theme, progress }) => `
    height: 100%;
    width: 100%;
    background-color: ${theme.colors.teal1};
    transform: translateX(-${100 - progress}%);
  `,
);
